import classnames from 'classnames'
import { formatRelative } from 'date-fns'
import React from 'react'

import { formatCryptoAmount, formatFiatAmount } from '../../lib/helpers'
import { CryptoIcon } from '../crypto-icon'
import BankIcon from './bank-icon.svg'

export interface AccountItemProps {
    /** Account name **/
    name: string
    /** Balance information **/
    balanceInfo?: {
        current: number
        isoCurrencyCode?: string
    }
    /** Date of last update information **/
    lastUpdatedOne?: Date
    /** This indicates when it is crypto and need to look for a specific icon */
    isCrypto?: boolean
    /** Tell the component if it is selected or active */
    active?: boolean
    /** Tell the component if it is inactive    */
    inactive?: boolean
    /** length of accounts list **/
    length?: number
    /** Fill the space with width */
    fill?: boolean
    /** Role */
    role?: string
    /** Aria row index */
    ariaRowIndex?: number
    /** Optional icon name */
    iconName?: string | undefined
    /** onClick handler optional */
    onClick?: () => void
    className?: string
}

/**
 * Component to display the information of a certain account
 */
export function AccountItem({
    name,
    iconName,
    balanceInfo,
    lastUpdatedOne = undefined,
    isCrypto = false,
    active = true,
    inactive = false,
    fill = false,
    ariaRowIndex = 0,
    length = undefined,
    onClick = () => {},
    className,
    ...props
}: AccountItemProps) {
    const wrapperClasses = classnames(
        'group',
        'bg-white rounded-sm',
        'border border-gray6 box-border',
        'shadow-md',
        'transition-opacity',
        { 'opacity-50': !active, 'w-full': fill },
        'hover:opacity-100',
        className,
    )

    const bankNameText = [
        'not-italic',
        'font-bold',
        'text-sm leading-2 flex items-center',
    ].join(' ')
    const balanceTextStyles = [
        'font-light md:text-md',
        'text-xs leading-2 flex items-center',
        'w-full',
        'overflow-hidden truncate',
    ].join(' ')
    let timeDiff = ''
    if (lastUpdatedOne) {
        timeDiff = formatRelative(lastUpdatedOne, new Date())
    }

    const current =
        balanceInfo?.isoCurrencyCode === '$'
            ? formatFiatAmount(balanceInfo.current ?? 0)
            : formatCryptoAmount(balanceInfo?.current ?? 0)
    const balanceText = balanceInfo
        ? `${
              balanceInfo.isoCurrencyCode || ''
          } ${current} available as of ${timeDiff}`
        : 'Not synced yet'

    return (
        <div
            data-test-id='linked-account-item'
            className={wrapperClasses}
            {...props}
            aria-rowindex={ariaRowIndex}
            onClick={onClick}
            tabIndex={0}>
            <div className={'flex flex-row items-center w-full pr-4'}>
                <div className={'p-4 w-max'}>
                    {isCrypto ? (
                        <CryptoIcon
                            className={'w-8'}
                            name={iconName?.toLowerCase() ?? name.toLowerCase()}
                            active={active}
                        />
                    ) : (
                        <img src={BankIcon} alt={'bank icon'} />
                    )}
                </div>
                <div className='w-full truncate'>
                    <div
                        className={`flex flex-row ${
                            length ? 'justify-between' : ''
                        }`}
                        role='cell'>
                        <div className={bankNameText}>{name}</div>
                        {length && (
                            <div className={bankNameText}>
                                {length} account{length > 1 ? 's' : ''}
                            </div>
                        )}
                    </div>
                    <div className={`${balanceTextStyles}`} role='cell'>
                        {balanceText}
                    </div>
                </div>
            </div>
        </div>
    )
}
